import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import 'core-js/stable';
import 'regenerator-runtime/runtime'; // ✅ ← ADD THIS

// Polyfill Object.hasOwn for iOS 12 / older browsers (ES2022 feature)
if (!Object.hasOwn) {
  Object.hasOwn = function(obj, prop) {
    return Object.prototype.hasOwnProperty.call(obj, prop);
  };
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.js';
import './index.css';
import './App.css';
import './components/Login.css';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './context/AuthProvider';
import { WhatsAppProvider } from './context/WhatsAppContext';
import { SocketProvider } from './context/SocketContext';
import { SessionManagerProvider } from './context/SessionManagerContext';
import { NotificationProvider } from './context/NotificationContext';
import { ToastProvider } from './context/ToastContext';
import * as swRegistration from './ServiceWorkerRegistration';
// ResizeObserver polyfill
import RO from 'resize-observer-polyfill';

console.log('✅ React & polyfills loaded — React.version:', React.version);

// Safely assign ResizeObserver for browsers like iOS 12 Safari
if (typeof window !== 'undefined') {
  if (typeof window.ResizeObserver === "undefined") {
    console.log("⚠️ Using ResizeObserver polyfill");
    window.ResizeObserver = RO;
  } else {
    console.log("👍 Native ResizeObserver supported");
  }
}

// Capture PWA install prompt globally — fires on first page load (Login screen)
// before MainLayout/InstallPrompt mount. Stored for components to pick up later.
window.__pwaInstallPrompt = null;
window.addEventListener('beforeinstallprompt', function (e) {
  e.preventDefault();
  window.__pwaInstallPrompt = e;
});

window.onerror = function (msg, url, lineNo, columnNo, error) {
  // Log to console instead of alert — alert() blocks the thread and can cause
  // infinite alert storms when combined with other blocking alerts (e.g. 401 interceptor).
  console.error('[window.onerror]', msg, url, lineNo, columnNo, error?.stack || '');
  return false;
};

// ── PWA session restore ─────────────────────────────────────────────────────
// Restores persisted auth from localStorage → sessionStorage, then calls
// the backend session-restore endpoint to re-register the device IP
// (mobile networks rotate IPs frequently). React is mounted ONLY after
// this completes so no component fires an API call before IP is registered.

function _bootApp() {
  // Pre-React cleanup: wipe stale session tokens on login page
  if (window.location.pathname === '/login' && !localStorage.getItem('auth')) {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('lu');
    sessionStorage.removeItem('auth');
  }

  var root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <AuthProvider>
      <WhatsAppProvider>
        <BrowserRouter>
          <ToastProvider>
            <SocketProvider>
              <SessionManagerProvider>
                <NotificationProvider>
                  <App />
                </NotificationProvider>
              </SessionManagerProvider>
            </SocketProvider>
          </ToastProvider>
        </BrowserRouter>
      </WhatsAppProvider>
    </AuthProvider>
  );
}

// Check if we need to restore a session from localStorage
var _needsRestore = false;
if (!sessionStorage.getItem('token')) {
  try {
    var saved = localStorage.getItem('auth');
    if (saved) {
      var _auth = JSON.parse(saved);
      if (_auth.token && _auth.username) {
        var _roles = _auth.roles || [];
        var _isPrivileged = _roles.indexOf('owner') !== -1 || _roles.indexOf('solution') !== -1
          || _roles.indexOf('saadhak') !== -1 || _roles.indexOf('saadhakdashboard') !== -1;
        var _hasRemoteAccess = !!_auth.allowedOutside || !!_auth.limitedRemoteAccess;
        var _ua = navigator.userAgent || '';
        var _isMobile = /iPhone|Windows Phone/i.test(_ua) || (/Android/i.test(_ua) && /Mobile/i.test(_ua));
        if (_isPrivileged || _hasRemoteAccess || _isMobile) {
          sessionStorage.setItem('token', _auth.token);
          sessionStorage.setItem('lu', _auth.username);
          _needsRestore = true;
        } else {
          localStorage.removeItem('auth');
        }
      }
    }
  } catch (_e) {}
}

if (_needsRestore) {
  // Call session-restore to re-register IP BEFORE React mounts.
  // This prevents the race where components fire API calls → IP mismatch → 401.
  var _apiBase = process.env.REACT_APP_URL || '';
  var _restoredAuth = JSON.parse(localStorage.getItem('auth') || '{}');
  fetch(_apiBase + '/users/session-restore', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + _restoredAuth.token,
      'x-username': _restoredAuth.username || '',
    },
  })
    .then(function (r) {
      if (r.status === 401) {
        // Token genuinely expired/invalid — clear so user sees clean login
        localStorage.removeItem('auth');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('lu');
        sessionStorage.removeItem('auth');
      }
      // 404 (endpoint not deployed), 500, etc. — proceed with restored tokens
    })
    .catch(function () { /* network error — proceed anyway, geo-fence is backup */ })
    .then(function () { _bootApp(); swRegistration.register(); });
} else {
  _bootApp();
  swRegistration.register();
}